import type { JSX } from 'preact';
import { useState, useRef } from 'preact/hooks';

// ─── persistence ──────────────────────────────────────────────────────────────

// v2 key — v1 was the old ambient-hint system
const ONBOARDING_KEY = '_ob2';

function hasSeenOnboarding(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(ONBOARDING_KEY) === '1';
  } catch {
    return false;
  }
}

function markOnboardingSeen(): void {
  try {
    localStorage.setItem(ONBOARDING_KEY, '1');
  } catch {
    /* ignore */
  }
}

// ─── slide definitions ────────────────────────────────────────────────────────

type Slide = {
  id: string;
  Icon: () => JSX.Element;
  title: string;
  body: string;
};

function WelcomeIcon() {
  return (
    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" aria-hidden="true">
      <rect x="8" y="10" width="28" height="40" rx="4" fill="var(--accent-soft, var(--surface-2))" stroke="var(--accent)" stroke-width="2"/>
      <rect x="28" y="10" width="28" height="40" rx="4" fill="var(--surface-2)" stroke="var(--accent)" stroke-width="2"/>
      <line x1="32" y1="10" x2="32" y2="50" stroke="var(--accent)" stroke-width="1.5"/>
      <line x1="14" y1="22" x2="30" y2="22" stroke="var(--accent)" stroke-width="1.5" stroke-linecap="round"/>
      <line x1="14" y1="28" x2="30" y2="28" stroke="var(--accent)" stroke-width="1.5" stroke-linecap="round"/>
      <line x1="14" y1="34" x2="26" y2="34" stroke="var(--accent)" stroke-width="1.5" stroke-linecap="round"/>
      <line x1="34" y1="22" x2="50" y2="22" stroke="var(--ink-muted)" stroke-width="1.5" stroke-linecap="round"/>
      <line x1="34" y1="28" x2="50" y2="28" stroke="var(--ink-muted)" stroke-width="1.5" stroke-linecap="round"/>
      <line x1="34" y1="34" x2="46" y2="34" stroke="var(--ink-muted)" stroke-width="1.5" stroke-linecap="round"/>
    </svg>
  );
}

// Navigate slide — mock header + cm-pill pointing up at it
function NavigateIcon() {
  return (
    <div class="coach-callout-preview" aria-hidden="true">
      <div class="coach-mock-header">Matthew · 3</div>
      <span class="cm-pill" data-dir="up" data-caret="center">
        <span class="cm-arrow">↑</span> Pick a chapter
      </span>
    </div>
  );
}

// Listen slide — cm-pill pointing down at a mock play button
function ListenIcon() {
  return (
    <div class="coach-callout-preview" aria-hidden="true">
      <span class="cm-pill" data-dir="down" data-caret="center">
        <span class="cm-arrow">↓</span> Tap to listen
      </span>
      <div class="coach-mock-player">
        {/* play circle */}
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="18" cy="18" r="16"/>
          <path d="M15 12l10 6-10 6V12z" style="fill:currentColor;stroke:none"/>
        </svg>
      </div>
    </div>
  );
}

// Verse slide — mock verse text + cm-pill pointing up at it
function VerseIcon() {
  return (
    <div class="coach-callout-preview" aria-hidden="true">
      <div class="coach-mock-verse">
        <span class="coach-mock-versenum">3</span>
        <span class="coach-mock-versetext">In the beginning was the Word, and the Word was with God…</span>
      </div>
      <span class="cm-pill" data-dir="up" data-caret="center">
        <span class="cm-arrow">↑</span> Tap any verse
      </span>
    </div>
  );
}

// Plans slide — row of day circles (done/done/today/upcoming)
function PlansIcon() {
  return (
    <div class="coach-callout-preview" aria-hidden="true">
      <div class="coach-mock-plan-row">
        <div class="coach-mock-day is-done">✓</div>
        <div class="coach-mock-day is-done">✓</div>
        <div class="coach-mock-day is-today">3</div>
        <div class="coach-mock-day">4</div>
        <div class="coach-mock-day">5</div>
      </div>
      <span class="cm-pill" data-dir="up" data-caret="center">
        <span class="cm-arrow">↑</span> Today's reading
      </span>
    </div>
  );
}

const SLIDES: Slide[] = [
  {
    id: 'welcome',
    Icon: WelcomeIcon,
    title: 'Welcome to Scripture',
    body: 'An audio-first Bible reader designed for every language and every phone.',
  },
  {
    id: 'navigate',
    Icon: NavigateIcon,
    title: 'Go anywhere in the Bible',
    body: 'Tap the book title at the top to pick any book and chapter. Your last place is always saved.',
  },
  {
    id: 'listen',
    Icon: ListenIcon,
    title: 'Hear it read aloud',
    body: "Press play and follow along — each verse lights up as it’s spoken so you never lose your place.",
  },
  {
    id: 'verse',
    Icon: VerseIcon,
    title: 'Tap any verse',
    body: "Share, copy, bookmark, highlight in color, or ask a question about what you're reading.",
  },
  {
    id: 'plans',
    Icon: PlansIcon,
    title: 'Follow a reading plan',
    body: 'Stay on track with daily plans that guide you through the Bible — one chapter at a time.',
  },
];

// ─── component ────────────────────────────────────────────────────────────────

export function OnboardingCoach() {
  const [visible, setVisible] = useState(() => !hasSeenOnboarding());
  const [step, setStep] = useState(0);
  const swipeStartX = useRef<number | null>(null);

  if (!visible) return null;

  const total = SLIDES.length;
  const isLast = step === total - 1;

  const goTo = (i: number): void => {
    setStep(Math.max(0, Math.min(total - 1, i)));
  };

  const complete = (): void => {
    markOnboardingSeen();
    setVisible(false);
  };

  const advance = (): void => {
    if (isLast) complete();
    else goTo(step + 1);
  };

  // Swipe support
  const onPointerDown = (e: PointerEvent): void => {
    swipeStartX.current = e.clientX;
  };
  const onPointerUp = (e: PointerEvent): void => {
    if (swipeStartX.current === null) return;
    const delta = swipeStartX.current - e.clientX;
    if (Math.abs(delta) > 40) {
      if (delta > 0) goTo(step + 1);
      else goTo(step - 1);
    }
    swipeStartX.current = null;
  };

  // step is always clamped to [0, total-1] by goTo()
  const slide = SLIDES[step]!;

  return (
    <div
      class="coach-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Getting started"
    >
      <div
        class="coach-sheet"
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
      >
        {/* skip */}
        <div class="coach-skip-row">
          <button
            type="button"
            class="coach-skip"
            onClick={complete}
            aria-label="Skip introduction"
          >
            Skip
          </button>
        </div>

        {/* slide content */}
        <div class="coach-slide" aria-live="polite" aria-atomic="true">
          <div class="coach-slide-icon">
            <slide.Icon />
          </div>
          <h2 class="coach-slide-title">{slide.title}</h2>
          <p class="coach-slide-body">{slide.body}</p>
        </div>

        {/* progress pills */}
        <div class="coach-pills" role="tablist" aria-label="Progress">
          {SLIDES.map((s, i) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              class={`coach-pill${i === step ? ' is-active' : ''}`}
              aria-label={`Step ${i + 1} of ${total}`}
              aria-selected={i === step}
              onClick={() => goTo(i)}
            />
          ))}
        </div>

        {/* primary CTA */}
        <button
          type="button"
          class="coach-next"
          onClick={advance}
        >
          {isLast ? 'Get started' : 'Next'}
        </button>
      </div>
    </div>
  );
}
